import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useErrorBoundary, useMixpanel, useWorkspace } from '@treams/treams-hooks';
import {
	DismissFeatureType,
	useDismissFeatures,
} from '@treams/treams-hooks/src/useDismissFeatures';
import { DocumentIcon, MagicWandIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	FlexGap,
	GraphQLErrorMessage,
	IconWrapper,
	Modal,
	Paragraph,
	Popover,
	Subheading,
} from '@treams/treams-ui';

type Props = {
	submission: graphql.EvaluationSubmission;
	onDismiss: () => void;
	successCallBack: () => void;
};

export const EvaluationSubmissionAIInsightsApproval = (props: Props) => {
	const workspace = useWorkspace();

	const mixpanel = useMixpanel();

	const { dismissFeature, removeFeatureDismissed } = useDismissFeatures();

	const { t } = useTranslation();

	const { data, loading } = graphql.useWorkspaceFeaturesQuery({
		variables: {
			id: workspace.id,
		},
	});
	const [approve, { loading: approving }] = graphql.useApproveFeatureForViewerMutation();

	const [error, attempt] = useErrorBoundary();

	const feature = data?.workspace.features.find(
		(feature) => feature.feature.id === graphql.FeatureName.ConversationAiInsights,
	);

	if (loading || !feature || !feature.requiredConditions) {
		return null;
	}

	const approveFeatureConditions = attempt(async (conditionsVersion: string) => {
		const result = await approve({
			variables: {
				input: {
					featureID: graphql.FeatureName.ConversationAiInsights,
					conditionsVersion,
				},
			},
		});

		if (result.data?.approveFeatureForViewer.approval) {
			props.onDismiss();
			props.successCallBack();

			removeFeatureDismissed(
				DismissFeatureType.FEATURE_APPROVAL_MODAL,
				graphql.FeatureName.ConversationAiInsights,
			);

			mixpanel?.track('feature.approved', {
				featureName: graphql.FeatureName.ConversationAiInsights,
				targetType: graphql.FeatureApprovalTargetType.User,
			});
		}
	});

	const onDismiss = () => {
		dismissFeature(
			DismissFeatureType.FEATURE_APPROVAL_MODAL,
			graphql.FeatureName.ConversationAiInsights,
		);

		props.onDismiss();

		mixpanel?.track('feature.dismissed', {
			featureName: graphql.FeatureName.ConversationAiInsights,
			targetType: graphql.FeatureApprovalTargetType.User,
		});
	};

	return (
		<Popover center visible>
			<Modal>
				<GraphQLErrorMessage error={error} />
				<Modal.Header
					title={t('evaluation.submission.aiInsights.approval.modal.title')}
					onClose={onDismiss}
				/>
				<Modal.Contents>
					<FlexGap gap={8} justifyContent="stretch" flexDirection={['column', 'row']}>
						<Box
							p={16}
							pt={32}
							flexGrow={1}
							width={['100%', '50%']}
							backgroundColor="white"
							style={{ borderRadius: '8px', border: '1px solid #D9E0E7' }}
						>
							<FlexGap flexDirection="column" gap={10} alignItems="center" height="100%">
								<IconWrapper size={32} color="black">
									<MagicWandIcon />
								</IconWrapper>
								<Subheading>
									{t('evaluation.submission.aiInsights.approval.modal.assistance.title')}
								</Subheading>
								<Flex justifyContent="center" flexGrow={1} alignItems="flex-start">
									<Paragraph textAlign="center">
										{t('evaluation.submission.aiInsights.approval.modal.assistance.description')}
									</Paragraph>
								</Flex>
								<Button
									title={t('evaluation.submission.aiInsights.approval.modal.assistance.button')}
									onClick={() =>
										approveFeatureConditions(feature.requiredConditions!.currentVersion)
									}
									width="100%"
									type="transparent"
									mt={20}
									loading={approving}
								/>
							</FlexGap>
						</Box>
						<Box p={16} pt={32} flexGrow={1} width={['100%', '50%']}>
							<FlexGap flexDirection="column" gap={10} alignItems="center" height="100%">
								<IconWrapper size={32} color="black">
									<DocumentIcon />
								</IconWrapper>
								<Subheading>
									{t('evaluation.submission.aiInsights.approval.modal.noAssistance.title')}
								</Subheading>
								<Flex justifyContent="center" flexGrow={1} alignItems="flex-start">
									<Paragraph textAlign="center">
										{t('evaluation.submission.aiInsights.approval.modal.noAssistance.description')}
									</Paragraph>
								</Flex>
								<Button
									title={t('evaluation.submission.aiInsights.approval.modal.noAssistance.button')}
									onClick={onDismiss}
									width="100%"
									type="secondary"
									mt={20}
								/>
							</FlexGap>
						</Box>
					</FlexGap>
					<Paragraph mt={2}>
						<a
							style={{ textDecoration: 'underline' }}
							href={feature.requiredConditions!.linkToConditions}
							target="_blank"
							rel="noreferrer"
						>
							{t('evaluation.submission.aiInsights.approval.modal.disclaimer')}
						</a>
					</Paragraph>
				</Modal.Contents>
			</Modal>
		</Popover>
	);
};
